/**
 * @file src/config/audioOffset.js
 * @description Per-device MANUAL audio offset (ms) — the by-ear latency calibration.
 *
 * WHY THIS EXISTS
 * ---------------
 * A device's true acoustic output latency (how long after we schedule a sound it actually leaves
 * the speaker) cannot be reliably measured or inferred in the browser. `AudioContext.outputLatency`
 * is non-standard (absent on e.g. Safari) and, where present, under-reports on some platforms — iOS
 * has been observed reporting ~12.6 ms when a microphone measured ~125 ms. There is no automatic
 * signal we can trust to compensate it.
 *
 * So we give the user a MANUAL escape hatch: a single per-device number, in milliseconds, that they
 * dial by EAR until two devices playing the same shared session line up acoustically. A POSITIVE
 * offset means "this device's speaker is N ms late" → fire its audio N ms EARLIER so the sound
 * leaves the speaker on the beat instead of one output-latency later.
 *
 * This is a per-DEVICE value (all voices on the device share it), not per-track or per-session. It
 * only matters for cross-device acoustic alignment (synced sessions); when playing solo it shifts
 * this device's own audio uniformly, which is inaudible.
 *
 * The wording comes from the sync spike that validated this control, and is kept because it is
 * still the clearest statement of what the number does:
 *   "Manual audio offset (ms) — fires this device's clicks earlier — dial until two devices line up
 *    acoustically."
 *
 * The value is applied to the sink timing in `AudioEngineAdapter` (the bar-quantized start), which
 * is where an orbiter's audio joins the shared timeline.
 *
 * One moving part was added later: the audio buffer became something a person can choose, and the
 * buffer is part of the lateness this number cancels. So a stored offset also remembers the buffer
 * it was dialled against and is corrected when that changes — see `driftCorrectionMs`.
 *
 * This module deliberately does NOT import `audio/audioContextBoot.js` to learn the buffer, even
 * though that is where it is known. Importing it RUNS it — it installs the audio context as a module
 * side effect — which would drag a real Tone context into every consumer and every unit test that
 * only wanted an integer. The boot module pushes the number here instead, via
 * `noteAudioContextBaseLatencyMs`, so this file stays free of audio dependencies.
 */

/** localStorage key holding the persisted per-device offset (ms). */
const STORAGE_KEY = 'audioOffsetMs';
/**
 * localStorage key holding the device callback length, in ms, that was in force when the offset was
 * dialled in — see `driftCorrectionMs` below for what it is for.
 */
const BASE_LATENCY_KEY = 'audioOffsetBaseLatencyMs';
/**
 * localStorage key holding the BUFFER the offset was dialled against (`interactive` / `balanced` /
 * `playback`). Stored next to the measurement because the measurement alone cannot say WHY it
 * changed — see `driftCorrectionMs`.
 */
const LATENCY_HINT_KEY = 'audioOffsetLatencyHint';
/** URL param that pre-loads / overrides the offset for a page load (bookmark per device). */
const URL_PARAM = 'audioOffset';
/**
 * Bound on the magnitude, in ms. ±500 ms covers integrated-speaker + Bluetooth output latencies
 * (Bluetooth can be 150–300 ms), the same range the sync spike exposed.
 */
export const MAX_ABS_OFFSET_MS = 500;

/** Event dispatched on `window` whenever the offset changes, so any UI can reflect it live. */
export const AUDIO_OFFSET_CHANGED_EVENT = 'orbiters:audio-offset-changed';

/** Clamp to a finite, integer ms value within ±MAX_ABS_OFFSET_MS. Non-numbers → 0. */
export function clampAudioOffsetMs(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n)) return 0;
  return Math.max(-MAX_ABS_OFFSET_MS, Math.min(MAX_ABS_OFFSET_MS, Math.round(n)));
}

function readStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function readUrl() {
  try {
    if (typeof window === 'undefined') return null;
    const raw = new URLSearchParams(window.location?.search ?? '').get(URL_PARAM);
    return raw == null || raw === '' ? null : raw;
  } catch {
    return null;
  }
}

/**
 * The device callback length, in ms, the context was actually built with — or null before the audio
 * context has booted, or where the browser does not report it. It is the MEASURED size, not the one
 * implied by the preference, because the browser chooses the real size and only reports it back.
 */
let installedBaseLatencyMs = null;
/** The `latencyHint` the context was built with — the BUFFER, as opposed to its measured length. */
let installedLatencyHint = null;

/**
 * Told to us by `audio/audioContextBoot.js` once it has built the context. Push rather than pull so
 * this module never has to import — and therefore run — the boot module.
 * @param {number|null|undefined} ms the measured device callback length
 * @param {string|null|undefined} [latencyHint] the buffer that was asked for
 */
export function noteAudioContextBaseLatencyMs(ms, latencyHint) {
  // `Number(null)` is 0, and 0 is a perfectly finite latency — so "no reading" has to be rejected
  // before the numeric check, or an unreported latency would be stored as a real measurement of
  // zero and every later offset would be corrected against it.
  if (ms === null || ms === undefined || ms === '') {
    installedBaseLatencyMs = null;
  } else {
    const value = Number(ms);
    installedBaseLatencyMs = Number.isFinite(value) ? value : null;
  }
  installedLatencyHint = typeof latencyHint === 'string' && latencyHint ? latencyHint : null;
}

function currentBaseLatencyMs() {
  return installedBaseLatencyMs;
}

function readStoredBaseLatencyMs() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(BASE_LATENCY_KEY);
    if (raw == null || raw === '') return null;
    const value = Number(raw);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function readStoredLatencyHint() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    const raw = window.localStorage.getItem(LATENCY_HINT_KEY);
    return raw == null || raw === '' ? null : raw;
  } catch {
    return null;
  }
}

/**
 * How much to shift a stored offset because the audio BUFFER changed since it was dialled in.
 *
 * Part of the lateness this offset cancels IS the buffer, and the buffer is now something
 * someone can change at will. A number dialled by ear on one buffer is wrong on another — by about
 * 4 ms between the middle presets and about 17 ms at the extreme — and two people in a room on
 * different buffers no longer share a floor, which they always did when everyone was silently on
 * the same one.
 *
 * A bigger buffer means the speaker is later, and a POSITIVE offset means "this device is late,
 * fire earlier" — so the correction is simply how much later the device now is. Only the part the
 * browser can actually measure is touched; the by-ear remainder (speaker, Bluetooth, the room) is
 * left exactly as the person set it, because nothing can measure that.
 *
 * ⚠️ It is gated on the stored `latencyHint`, NOT on the measurement alone, and the difference is
 * the whole correctness of this function. `baseLatency` moves when the OUTPUT DEVICE changes too:
 * plug in Bluetooth headphones after tuning on laptop speakers and the browser reports a different
 * callback length though nobody touched the buffer. Correcting there would move a number that was
 * dialled for that very output path, for a reason this feature has nothing to do with. Same buffer
 * ⇒ no correction, whatever the measurement says.
 *
 * Returns 0 whenever anything is unknown, which is the honest answer: an offset from before this
 * existed has no recorded buffer, and guessing one would move a number somebody tuned.
 */
function driftCorrectionMs() {
  const storedHint = readStoredLatencyHint();
  if (storedHint === null || installedLatencyHint === null) return 0;
  if (storedHint === installedLatencyHint) return 0;

  const stored = readStoredBaseLatencyMs();
  const current = currentBaseLatencyMs();
  if (stored === null || current === null) return 0;
  return current - stored;
}

/**
 * The live cached offset (ms). Resolved lazily on first read: URL param wins (per-load override),
 * then persisted localStorage corrected for any buffer change since it was set, else 0. Cached so
 * the hot audio path never touches storage/URL.
 */
let cachedOffsetMs = null;

function resolveInitial() {
  // The URL is a deliberate per-load instruction — someone stating the number for THIS load — so it
  // is taken as given. Only the remembered value can have gone stale.
  const fromUrl = readUrl();
  if (fromUrl != null) return clampAudioOffsetMs(fromUrl);
  const fromStorage = readStorage();
  // `!= null` alone would let through an empty string, whose `Number('')` is 0 — so a blank key
  // would resolve to the pure drift correction rather than to no offset at all.
  if (fromStorage != null && fromStorage !== '') {
    return clampAudioOffsetMs(Number(fromStorage) + driftCorrectionMs());
  }
  return 0;
}

/**
 * The current per-device manual audio offset, in ms (integer, ±MAX_ABS_OFFSET_MS). Positive = fire
 * earlier. Cheap: reads a module-cached number after the first resolve.
 * @returns {number}
 */
export function getManualAudioOffsetMs() {
  if (cachedOffsetMs === null) cachedOffsetMs = resolveInitial();
  return cachedOffsetMs;
}

/** The offset expressed in SECONDS, for subtracting from audio-clock scheduling. */
export function getManualAudioOffsetSec() {
  return getManualAudioOffsetMs() / 1000;
}

/**
 * Set the per-device offset (ms). Persists to localStorage by default and notifies listeners via
 * the `AUDIO_OFFSET_CHANGED_EVENT` window event. Takes effect on the NEXT scheduled start.
 * @param {number} ms
 * @param {{ persist?: boolean }} [opts]
 * @returns {number} the clamped value actually stored
 */
export function setManualAudioOffsetMs(ms, { persist = true } = {}) {
  const next = clampAudioOffsetMs(ms);
  cachedOffsetMs = next;
  if (persist) {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(STORAGE_KEY, String(next));
        // Stamp the buffer this number was dialled against — both WHICH buffer and how long it
        // measured — so a later buffer change can be corrected for instead of silently invalidating
        // the tuning. Both are cleared rather than left stale when the context cannot be read: a
        // wrong stamp is worse than none, because it would shift a good number by a made-up amount.
        const baseLatency = currentBaseLatencyMs();
        if (baseLatency === null || installedLatencyHint === null) {
          window.localStorage.removeItem(BASE_LATENCY_KEY);
          window.localStorage.removeItem(LATENCY_HINT_KEY);
        } else {
          window.localStorage.setItem(BASE_LATENCY_KEY, String(baseLatency));
          window.localStorage.setItem(LATENCY_HINT_KEY, installedLatencyHint);
        }
      }
    } catch {
      // ignore persistence errors (private mode / disabled storage)
    }
  }
  try {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent(AUDIO_OFFSET_CHANGED_EVENT, { detail: { offsetMs: next } }));
    }
  } catch {
    // ignore (e.g. CustomEvent unavailable)
  }
  return next;
}

/**
 * Install a tiny runtime handle for by-ear tuning from the console/bookmarklet without a built UI:
 *   orbitersAudioOffset.set(90)   // fire 90 ms earlier, persisted
 *   orbitersAudioOffset.get()
 * Idempotent; safe to call at app bootstrap. No-op outside the browser.
 */
export function installAudioOffsetRuntimeHandle() {
  if (typeof window === 'undefined') return;
  window.orbitersAudioOffset = {
    get: getManualAudioOffsetMs,
    set: (ms) => setManualAudioOffsetMs(ms),
    maxAbsMs: MAX_ABS_OFFSET_MS,
  };
}

/** Test-only: reset the module cache so the next read re-resolves from URL/storage. */
export function __resetAudioOffsetCacheForTests() {
  cachedOffsetMs = null;
  installedBaseLatencyMs = null;
  installedLatencyHint = null;
}
